/**
 * Присутствие товаров макета на площадках — по карточкам, которые окно макета
 * уже грузит (`GET /designs/:code/marketplace-cards`: зеркала ВБ/ОЗОН/Маркета,
 * черновики ПИМ и сайт). Своего запроса нет: цепочка окна, точки в таблице
 * «Товары» и чип «Карточки» считаются из одного списка и не могут разойтись.
 */

import type { ChainTone } from '../../../../components/ui/EntityChain'
import type { EntityTarget } from '../../../../components/entity-view/navigatorContext'
import type { MarketplaceCardDto } from '../../../pipeline/api'
import { MARKETPLACES } from '../../../../shared/marketplaces'
import { PRESENCE_CHAIN_LABEL, PRESENCE_MARKETPLACES } from '../../../products-v2/presence'
import type { PresenceMarketplace } from '../../../products-v2/types'
import { cardState, isBlocked, stateText } from './marketplaceCardState'

export type PresenceChannel = PresenceMarketplace

/** Те же четыре канала и тот же порядок, что в окне товара: ВБ · ОЗ · ЯМ · Сайт. */
export const PRESENCE_CHANNELS: PresenceChannel[] = [...PRESENCE_MARKETPLACES]

/** Короткие подписи переключателя — общие с окном товара. */
export const PRESENCE_CHANNEL_LABEL: Record<PresenceChannel, string> = PRESENCE_CHAIN_LABEL

/** Полные названия площадок — из реестра, свой список не заводим. */
export const PRESENCE_CHANNEL_NAME: Record<PresenceChannel, string> = {
  wb: MARKETPLACES.wb.fullName,
  ozon: MARKETPLACES.ozon.fullName,
  ym: MARKETPLACES.ym.fullName,
  site: 'Сайт',
}

/** Карточка на витрине: площадка её показывает (с фото или без). */
export const isLiveCard = (card: MarketplaceCardDto): boolean => {
  const state = cardState(card)
  return state === 'live' || state === 'live_no_photo'
}

const channelOf = (card: MarketplaceCardDto): PresenceChannel | null =>
  (PRESENCE_CHANNELS as string[]).includes(card.marketplace) ? (card.marketplace as PresenceChannel) : null

/** Тон точки для набора карточек одного товара на одной площадке. */
export function cardsTone(cards: MarketplaceCardDto[]): ChainTone {
  if (cards.length === 0) return 'none'
  if (cards.some((card) => isBlocked(card))) return 'bad'
  if (cards.some(isLiveCard)) return 'ok'
  return 'info'
}

/** Карточки по артикулу и каналу. */
export function groupCards(
  cards: MarketplaceCardDto[],
): Map<string, Record<PresenceChannel, MarketplaceCardDto[]>> {
  const result = new Map<string, Record<PresenceChannel, MarketplaceCardDto[]>>()
  for (const card of cards) {
    const channel = channelOf(card)
    if (!channel) continue
    let entry = result.get(card.article)
    if (!entry) {
      entry = { wb: [], ozon: [], ym: [], site: [] }
      result.set(card.article, entry)
    }
    entry[channel].push(card)
  }
  return result
}

export interface ChannelAggregate {
  channel: PresenceChannel
  /** Товаров макета. */
  total: number
  /** Товаров с карточкой этой площадки (любой, включая черновик). */
  withCards: number
  /** Товаров на витрине этой площадки. */
  live: number
  /** Товаров, у которых карточка не уедет или упала. */
  blocked: number
  tone: ChainTone
  /** Подсказка: «На витрине 3 из 5 товаров; черновиков и прочих карточек: 1». */
  title: string
}

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod100 = n % 100
  const mod10 = n % 10
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

export const productsWord = (n: number): string => plural(n, 'товара', 'товаров', 'товаров')

/** Агрегат по каналу: «ВБ x/y». Считается по артикулам макета, а не по числу карточек. */
export function channelAggregates(
  cards: MarketplaceCardDto[],
  articles: string[],
): Record<PresenceChannel, ChannelAggregate> {
  const grouped = groupCards(cards)
  const total = articles.length
  const result = {} as Record<PresenceChannel, ChannelAggregate>
  for (const channel of PRESENCE_CHANNELS) {
    let withCards = 0
    let live = 0
    let blocked = 0
    for (const article of articles) {
      const list = grouped.get(article)?.[channel] ?? []
      if (list.length > 0) withCards += 1
      if (list.some(isLiveCard)) live += 1
      if (list.some((card) => isBlocked(card))) blocked += 1
    }
    const tone: ChainTone =
      withCards === 0
        ? 'none'
        : blocked > 0
          ? 'bad'
          : live === total && total > 0
            ? 'ok'
            : live > 0
              ? 'warn'
              : 'info'
    const parts =
      withCards === 0
        ? [`Карточек на площадке ${PRESENCE_CHANNEL_NAME[channel]} нет`]
        : [
            `На витрине ${live} из ${total} ${productsWord(total)}`,
            withCards > live ? `есть карточка, но не на витрине: ${withCards - live}` : '',
            blocked > 0 ? `не уедет или с ошибкой: ${blocked}` : '',
          ].filter(Boolean)
    result[channel] = { channel, total, withCards, live, blocked, tone, title: parts.join('; ') }
  }
  return result
}

/** Сколько товаров макета на витрине хотя бы одной площадки. */
export const liveArticleCount = (cards: MarketplaceCardDto[], articles: string[]): number => {
  const grouped = groupCards(cards)
  return articles.filter((article) => {
    const entry = grouped.get(article)
    return entry ? PRESENCE_CHANNELS.some((channel) => entry[channel].some(isLiveCard)) : false
  }).length
}

/** Кабинет для подсказки точки. */
const cabinetOf = (card: MarketplaceCardDto): string =>
  card.seller_name || `кабинет ${card.seller_id}`

/** Подсказка точки: по строке на карточку — «KSA: на витрине». */
export function dotTitle(channel: PresenceChannel, cards: MarketplaceCardDto[]): string {
  if (cards.length === 0) return `${PRESENCE_CHANNEL_NAME[channel]}: карточки нет`
  return cards
    .map((card) => `${PRESENCE_CHANNEL_NAME[channel]}, ${cabinetOf(card)}: ${stateText(card).label}`)
    .join('\n')
}

/**
 * Куда ведёт клик по карточке: зеркало площадки — окно карточки площадки,
 * черновик ПИМ — окно черновика. Ключ зеркала: ВБ — nmID, ОЗОН и Я.Маркет —
 * артикул продавца, сайт — артикул.
 *
 * Черновик открывается по `pim_card_id` строки, а не по её `id`: у строки зеркала
 * `id` — это номер зеркала, и по нему открывался чужой черновик. Строка — зеркало,
 * если у неё есть `mirror_id`, она на витрине или это карточка Маркета с номером
 * market_sku. Сервис старой версии `pim_card_id` не присылает; тогда строка без
 * признаков зеркала — черновик, и её `id` и есть `marketplace_cards.id`.
 */
export function cardTarget(card: MarketplaceCardDto): EntityTarget | null {
  if (card.marketplace === 'site') return { kind: 'mp_card', marketplace: 'site', key: card.article }
  if (card.marketplace !== 'wb' && card.marketplace !== 'ozon' && card.marketplace !== 'ym') return null
  const mirror =
    card.mirror_id != null || isLiveCard(card) || (card.marketplace === 'ym' && Boolean(card.external_id))
  if (mirror) {
    const key = card.marketplace === 'wb' ? card.external_id : card.article
    if (key) {
      return { kind: 'mp_card', marketplace: card.marketplace, sellerId: card.seller_id, key: String(key) }
    }
  }
  // Поле есть в ответе (даже null) — оно главное; нет поля — старый сервис, id строки.
  const draftId = 'pim_card_id' in card ? card.pim_card_id : mirror ? null : card.id
  if (draftId != null) {
    return { kind: 'draft', cardId: draftId, sellerId: card.seller_id, marketplace: card.marketplace }
  }
  return null
}
